//
// One record per explainable thing, keyed by a stable identifier. Every help
// surface reads these same records — the page-arrival popup, the first-use popup,
// explain mode and the steps of a tour — so a sentence is written once.
//
// scope: 'page' fires once on first arrival (centred, no selector);
//        'section' describes a whole side-menu section, marker only;
//        'element' carries an explain-mode marker on its selector;
//        'tour' is never marked and never fires by itself — a tour step only.
// Whether an item speaks by itself on first use is decided by the wiring
// (useExplainAction at the call site), never by a flag here.
// guideAnchor is the EN heading text of the guide section to jump to; the FR
// guide is line-parallel, so the page maps it across languages.
// menuSelector lists where a record's marker sits in the side menu, first visible
// one wins. Only the section the user is in is marked.
//
// seenWithWelcome stamps a record read as the welcome completes: the dashboard is
// where that tour happens, so its introduction would open on top of it. It stays
// reachable from its marker.
//
// `page` on an element record confines it to that route: one panel can serve two
// sub-pages (the controls panel is both a case's and a task's), and without it the
// wrong sub-page's explanation is offered on the other.
//

import pagePrivilegesNeeded from '../utils/pagePrivilegesNeeded';

const reportPrivileges = ['reportsSynopsis', 'reportsDetails', 'reportsHandle', 'reportsTalkToUsers'];

const iconRowsHtml = (translate, rows) => rows.map((row) => (
  `<div><img src="${row.icon}" alt=""><span>${
    translate(row.descKey, 'tutorial')}</span>${row.sampleSvg ?? ''}</div>`
)).join('');

// Colors and path mirror the Squircle rendered by CasesListsItem
const respondersJoinedSquircle = `
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M50 0C5 0 0 5 0 50s5 50 50 50 50-5 50-50S95 0 50 0"
      fill="var(--color-pxCaseHeaderSecondaryBg)"
      stroke="var(--color-pxPrimary)"
      stroke-opacity="0.3"
      stroke-width="1"
      vector-effect="non-scaling-stroke"
    />
    <text x="50" y="50" text-anchor="middle" dominant-baseline="central" font-size="58" fill="currentColor">1</text>
  </svg>
`;

// The cases-list columns described by the sorting explanation, with their icon.
const columnButtons = [
  { icon: '/images/svg/gui/sortNumbers.svg', descKey: 'colNumberDesc' },
  { icon: '/images/svg/gui/edit.svg', descKey: 'infoActivityDesc' },
  { icon: '/images/svg/gcat/people/users.svg', descKey: 'colUsersDesc' },
  { icon: '/images/svg/gui/checkThick.svg', descKey: 'casesStatusLabel' },
  {
    icon: '/images/svg/gcat/people/support.svg',
    descKey: 'colRespondersDesc',
    sampleSvg: respondersJoinedSquircle,
  },
  { icon: '/images/svg/gui/infoCircle.svg', descKey: 'colInfoDesc' },
];

const casesOrderingHtml = (translate) => `
  <p>${translate('casesFilterBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, columnButtons)}</div>
`;

// Case statuses with the icon shown next to each in the list.
const statusRows = [
  { icon: '/images/svg/gui/new2.svg', descKey: 'statusNewDesc' },
  { icon: '/images/svg/gui/ongoing.svg', descKey: 'statusOngoingDesc' },
  { icon: '/images/svg/gui/pointingHand.svg', descKey: 'statusWaitingYouDesc' },
  { icon: '/images/svg/gui/checkThick.svg', descKey: 'statusDoneDesc' },
];

// The info column's pictograms, in the order they always appear.
const infoRows = [
  { icon: '/images/svg/gui/trash.svg', descKey: 'statusRejectedDesc' },
  { icon: '/images/svg/gui/crossHair.svg', descKey: 'infoTaskDesc' },
  { icon: '/images/svg/gui/pause.svg', descKey: 'statusHoldDesc' },
  { icon: '/images/svg/gui/checkeredFlag.svg', descKey: 'statusUsersDoneDesc' },
  { icon: '/images/svg/gui/cross.svg', descKey: 'statusCancelledDesc' },
  { icon: '/images/svg/gui/logout.svg', descKey: 'statusLeftDesc' },
  { icon: '/images/svg/gui/star.svg', descKey: 'statusRatedDesc' },
  { icon: '/images/svg/gui/inbox.svg', descKey: 'statusInvitedDesc' },
];

const casesStatusHtml = (translate) => `
  <p>${translate('casesStatusBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, statusRows)}</div>
`;

// A task's own progress icons: same vocabulary as the cases Statuses column,
// worded for the work rather than for the report.
const taskStatusRows = [
  { icon: '/images/svg/gui/new2.svg', descKey: 'taskStatusNewDesc' },
  { icon: '/images/svg/gui/ongoing.svg', descKey: 'taskStatusOngoingDesc' },
  { icon: '/images/svg/gui/pointingHand.svg', descKey: 'statusWaitingYouDesc' },
  { icon: '/images/svg/gui/checkThick.svg', descKey: 'taskStatusDoneDesc' },
];

const tasksColumnsHtml = (translate) => `
  <p>${translate('tasksColumnsBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, taskStatusRows)}</div>
`;

const casesRespondersHtml = (translate) => `
  <p>${translate('casesRespondersColBody', 'tutorial')}</p>
`;

const casesInfoHtml = (translate) => `
  <p>${translate('casesInfoColBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, infoRows)}</div>
`;

// The case control buttons, with each label and what it does. Which ones show
// depends on the report's state (see HomePageCaseControls).
const controlButtons = [
  { icon: '/images/svg/gui/play.svg', labelKey: 'participate', descKey: 'ctrlHandleDesc' },
  { icon: '/images/svg/gui/check.svg', labelKey: 'closeCase', descKey: 'ctrlCompleteDesc' },
  { icon: '/images/svg/gui/crossHair.svg', labelKey: 'assign', descKey: 'ctrlAssignDesc' },
  { icon: '/images/svg/gui/checkThick.svg', labelKey: 'assignedToMe', labelType: 'tutorial', descKey: 'ctrlAssignedDesc' },
  { icon: '/images/svg/gui/addTask.svg', labelKey: 'task', descKey: 'ctrlTaskDesc' },
  { icon: '/images/svg/gui/logout.svg', labelKey: 'leave', descKey: 'ctrlLeaveDesc' },
  { icon: '/images/svg/gui/cross.svg', labelKey: 'cancel', descKey: 'ctrlCancelDesc' },
  { icon: '/images/svg/gui/login.svg', labelKey: 'rejoin', descKey: 'ctrlRejoinDesc' },
  { icon: '/images/svg/gui/playPause.svg', labelKey: 'resume', descKey: 'ctrlResumeDesc' },
];

const controlButtonsHtml = (translate) => `
  <p>${translate('caseControlsBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">
    ${controlButtons.map((button) => (
      `<div><img src="${button.icon}" alt=""><span><b>${translate(button.labelKey, button.labelType)}</b>: ${translate(button.descKey, 'tutorial')}</span></div>`
    )).join('')}
  </div>
`;

// Shows the three-dots icon so the user knows what to hover a message for.
const messageMenuHtml = (translate) => `
  <div class="px-tutorial-cols">
    <div><img src="/images/svg/gui/dotdotdot.svg" alt=""><span>${translate('msgMenuBody', 'tutorial')}</span></div>
  </div>
`;

// The controls that appear once a voice recording has started, in order of use.
const audioControls = [
  { icon: '/images/svg/gui/stop.svg', descKey: 'audioStopDesc' },
  { icon: '/images/svg/gui/play.svg', descKey: 'audioPlayDesc' },
  { icon: '/images/svg/gui/cross.svg', descKey: 'audioDeleteDesc' },
  { icon: '/images/svg/gui/send.svg', descKey: 'audioSendDesc' },
];

const audioControlsHtml = (translate) => `
  <p>${translate('audioBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, audioControls)}</div>
`;

// The columns of the QR codes table, in display order.
const qrColumnKeys = [
  'qrColSelectDesc',
  'qrColFolderQrDesc',
  'qrColTemplateDesc',
  'qrColIdentifierDesc',
  'qrColStatusDesc',
];

const qrColumnsHtml = (translate) => `
  <p>${translate('qrsListBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">
    ${qrColumnKeys.map((key) => `<div><span>${translate(key, 'tutorial')}</span></div>`).join('')}
  </div>
`;

// The two checkboxes toggling whether folders and QR codes show in the list.
const qrDisplayToggles = [
  { icon: '/images/svg/gui/folders.svg', descKey: 'qrToggleFoldersDesc' },
  { icon: '/images/svg/gui/qr.svg', descKey: 'qrToggleQrsDesc' },
];

const qrDisplayTogglesHtml = (translate) => `
  <p>${translate('qrTogglesBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, qrDisplayToggles)}</div>
`;

// Folder controls: the actions on the selected folder, then what opens for editing.
const qrFolderControls = [
  { icon: '/images/svg/gui/tableCloseAll.svg', descKey: 'qrFolderCollapseDesc' },
  { icon: '/images/svg/gui/plus.svg', descKey: 'qrFolderCreateDesc' },
  { icon: '/images/svg/gui/edit.svg', descKey: 'qrFolderEditDesc' },
  { icon: '/images/svg/gui/trash.svg', descKey: 'qrFolderDeleteDesc' },
];

const qrFolderEditControls = [
  { icon: '/images/svg/gui/cross.svg', descKey: 'qrFolderCancelDesc' },
  { icon: '/images/svg/gui/check.svg', descKey: 'qrFolderConfirmDesc' },
  { icon: '/images/svg/gui/moveFolder.svg', descKey: 'qrFolderMoveDesc' },
];

const qrFolderControlsHtml = (translate) => `
  <p>${translate('qrFolderControlsBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, qrFolderControls)}</div>
  <p>${translate('qrFolderEditBody', 'tutorial')}</p>
  <div class="px-tutorial-cols">${iconRowsHtml(translate, qrFolderEditControls)}</div>
`;

const qrsListPageHtml = (translate) => `
  <p>${translate('qrsIntroBody', 'tutorial')}</p>
  <p>${translate('qrsSubListBody', 'tutorial')}</p>
`;

// The three Aide entries, in menu order.
const helpMenuHtml = (translate) => `
  <p>${translate('moreTrainingBody', 'tutorial')}</p>
  <ul class="px-tutorial-list">
    <li><b>${translate('explainLabel', 'tutorial')}</b> — ${translate('helpExplainDesc', 'tutorial')}</li>
    <li><b>${translate('tutorials')}</b> — ${translate('helpTutorialsDesc', 'tutorial')}</li>
    <li><b>${translate('guideLabel', 'tutorial')}</b> — ${translate('helpGuideDesc', 'tutorial')}</li>
  </ul>
`;

// Shows the marker as it looks on screen, so the step names it by sight.
const welcomeExplainHtml = (translate) => translate(
  'welcomeExplainBody',
  'tutorial',
  { badge: '<span class="px-tutorial-badge">?</span>' },
).split('\n').map((line) => `<p>${line}</p>`).join('');

const explainRegistry = {
  // ---- Welcome (first login) ----
  'welcome.intro': { scope: 'tour', titleKey: 'welcomeTitle', bodyKey: 'welcomeBody' },
  'welcome.help': { scope: 'tour', titleKey: 'moreTrainingTitle', descriptionHtml: helpMenuHtml },
  'welcome.support': { scope: 'tour', titleKey: 'supportTitle', bodyKey: 'supportBody' },
  'welcome.findHelp': { scope: 'tour', titleKey: 'welcomeFindHelpTitle' },
  'welcome.explain': { scope: 'tour', titleKey: 'welcomeExplainTitle', descriptionHtml: welcomeExplainHtml },

  // ---- Sections (marker on the main side-menu entry) ----
  'qrs': {
    scope: 'section', page: 'qrs', menuSelector: ['#qrs'], titleKey: 'menuQrsTitle', bodyKey: 'menuQrsBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Your QR codes',
  },
  'cases': {
    scope: 'section', page: 'cases', menuSelector: ['#cases'], titleKey: 'menuCasesTitle', bodyKey: 'menuCasesBody', requires: reportPrivileges, guideAnchor: 'Reports',
  },
  'tasks': {
    scope: 'section', page: 'tasks', menuSelector: ['#tasks'], titleKey: 'menuTasksTitle', bodyKey: 'menuTasksBody', guideAnchor: 'Tasks',
  },

  // ---- Pages (first arrival) ----
  'home.dashboard': {
    scope: 'page', menuSelector: ['#home'], page: 'home', seenWithWelcome: true, titleKey: 'pageDashboardTitle', bodyKey: 'pageDashboardBody', guideAnchor: 'Dashboard',
  },
  'home.cases': {
    scope: 'page', menuSelector: ['#sub_list'], page: 'cases/list', titleKey: 'pageCasesTitle', bodyKey: 'pageCasesBody', requires: reportPrivileges, guideAnchor: 'Reports',
  },
  'home.tasks': {
    scope: 'page', menuSelector: ['#sub_list'], page: 'tasks/list', titleKey: 'pageTasksTitle', bodyKey: 'pageTasksBody', guideAnchor: 'Tasks',
  },
  'qrs.list': {
    scope: 'page', menuSelector: ['#sub_list'], page: 'qrs/list', titleKey: 'qrsIntroTitle', descriptionHtml: qrsListPageHtml, requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Your QR codes',
  },
  'qrs.models': {
    scope: 'page', menuSelector: ['#sub_models'], page: 'qrs/models', titleKey: 'qrsSubModelsTitle', bodyKey: 'qrsSubModelsBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Overview: folders and templates',
  },
  'qrs.printing': {
    scope: 'page', menuSelector: ['#sub_printing'], page: 'qrs/printing', titleKey: 'qrsSubPrintingTitle', bodyKey: 'qrsSubPrintingBody', requires: ['qrManage'], guideAnchor: 'Printing',
  },
  'stats': {
    scope: 'page', menuSelector: ['#sub_stats'], page: 'cases/stats', titleKey: 'menuStatsTitle', bodyKey: 'menuStatsBody', requires: pagePrivilegesNeeded.stats, guideAnchor: 'Statistics',
  },
  'polls.settings': {
    scope: 'page', menuSelector: ['#polls'], page: 'polls/settings', titleKey: 'menuPollsTitle', bodyKey: 'menuPollsBody', requires: pagePrivilegesNeeded.polls, guideAnchor: 'Polls',
  },
  'comm.faq': {
    scope: 'page', menuSelector: ['#comm'], page: 'comm/faq', titleKey: 'menuCommTitle', bodyKey: 'menuCommBody', requires: pagePrivilegesNeeded.comms, guideAnchor: 'Publications',
  },
  'responders.teams': {
    scope: 'page', menuSelector: ['#responders'], page: 'responders/teams', titleKey: 'menuRespondersTitle', bodyKey: 'menuRespondersBody', requires: pagePrivilegesNeeded.responders, guideAnchor: 'Responders',
  },
  'users.members': {
    scope: 'page', menuSelector: ['#users'], page: 'users/members', titleKey: 'menuUsersTitle', bodyKey: 'menuUsersBody', requires: pagePrivilegesNeeded.users, guideAnchor: 'End users: anonymous and members',
  },
  'partners': {
    scope: 'page', menuSelector: ['#partners'], page: 'partners', titleKey: 'menuPartnersTitle', bodyKey: 'menuPartnersBody', guideAnchor: 'Partners',
  },
  'settings.custom': {
    scope: 'page', menuSelector: ['#settings'], page: 'settings/custom', titleKey: 'menuSettingsTitle', bodyKey: 'menuSettingsBody', guideAnchor: 'Visual identity',
  },

  // ---- Dashboard ----
  'home.dashboard.reports': {
    scope: 'element', selector: '#tutorialDashboardReports', titleKey: 'dashboardReportsTitle', bodyKey: 'dashboardReportsBody', guideAnchor: 'Summary cards',
  },
  'home.dashboard.recent': {
    scope: 'element', selector: '#tutorialDashboardRecent', titleKey: 'dashboardRecentTitle', bodyKey: 'dashboardRecentBody', guideAnchor: 'Recent reports',
  },
  'home.dashboard.supervised': {
    scope: 'element', selector: '#tutorialDashboardSupervised', titleKey: 'dashboardSupervisedTitle', bodyKey: 'dashboardSupervisedBody', guideAnchor: 'Summary cards',
  },
  'home.dashboard.tasks': {
    scope: 'element', selector: '#tutorialDashboardTasks', titleKey: 'dashboardTasksTitle', bodyKey: 'dashboardTasksBody', guideAnchor: 'Summary cards',
  },
  'home.dashboard.entrusted': {
    scope: 'element', selector: '#tutorialDashboardEntrusted', titleKey: 'dashboardEntrustedTitle', bodyKey: 'dashboardEntrustedBody', guideAnchor: 'Summary cards',
  },
  'home.dashboard.partnerships': {
    scope: 'element', selector: '#tutorialDashboardPartnerships', titleKey: 'dashboardPartnershipsTitle', bodyKey: 'dashboardPartnershipsBody', guideAnchor: 'Summary cards',
  },
  'home.dashboard.feedback': {
    scope: 'element', selector: '#tutorialDashboardFeedback', titleKey: 'dashboardFeedbackTitle', bodyKey: 'dashboardFeedbackBody', guideAnchor: 'Feedback',
  },

  // ---- Cases list ----
  'home.cases.list': {
    scope: 'element', selector: '#tutorialCasesList', titleKey: 'casesListTitle', bodyKey: 'casesListBody', requires: reportPrivileges, guideAnchor: 'The lifecycle of a report',
  },
  'home.cases.filter': {
    scope: 'element', selector: '#casesFilter', titleKey: 'casesFilterButtonTitle', bodyKey: 'casesFilterButtonBody', requires: reportPrivileges,
  },
  'home.cases.autoFilter': {
    scope: 'element', selector: '#casesAutoFilter', titleKey: 'casesAutoFilterTitle', bodyKey: 'casesAutoFilterBody', requires: reportPrivileges,
  },
  'home.cases.columns': {
    scope: 'element', selector: '#tutorialCasesFilter', titleKey: 'casesFilterTitle', descriptionHtml: casesOrderingHtml, requires: reportPrivileges,
  },
  'home.cases.importance': {
    scope: 'element', selector: '#groupLevels', titleKey: 'casesImportanceLabel', bodyKey: 'casesImportantColBody', requires: reportPrivileges,
  },
  'home.cases.status': {
    scope: 'element', selector: '#csClose', titleKey: 'casesStatusLabel', descriptionHtml: casesStatusHtml, requires: reportPrivileges,
  },
  'home.cases.responders': {
    scope: 'element', selector: '#csHandlers', titleKey: 'casesRespondersLabel', descriptionHtml: casesRespondersHtml, requires: reportPrivileges,
  },
  'home.cases.info': {
    scope: 'element', selector: '#casesInfo', titleKey: 'casesInfoLabel', descriptionHtml: casesInfoHtml, requires: reportPrivileges,
  },
  'home.cases.create': {
    scope: 'element', selector: '#caseCreate', titleKey: 'casesCreateTitle', bodyKey: 'casesCreateBody', requires: ['reportsHandle'], guideAnchor: 'The lifecycle of a report',
  },
  'home.cases.empty': { scope: 'tour', titleKey: 'emptyCasesTitle', bodyKey: 'emptyCasesBody', requires: reportPrivileges },
  'home.cases.noneSelected': { scope: 'tour', titleKey: 'noCaseSelectedTitle', bodyKey: 'noCaseSelectedBody' },

  // ---- Case details ----
  'home.cases.details': {
    scope: 'element', selector: '#tutorialCaseInfo', titleKey: 'caseInfoTitle', bodyKey: 'caseInfoBody', requires: reportPrivileges, guideAnchor: 'The lifecycle of a report',
  },
  'home.cases.badges': {
    scope: 'element', selector: '#tutorialCaseBadges', titleKey: 'caseBadgesTitle', bodyKey: 'caseBadgesBody', requires: reportPrivileges,
  },
  'home.cases.category': {
    scope: 'element', selector: '#tutorialCaseCategory', titleKey: 'caseCategoryTitle', bodyKey: 'caseCategoryBody', requires: reportPrivileges, guideAnchor: 'Report categories',
  },
  'home.cases.controls': {
    scope: 'element', page: 'cases/list', selector: '#tutorialCaseControls', titleKey: 'caseControlsTitle', descriptionHtml: controlButtonsHtml, requires: reportPrivileges, guideAnchor: 'Replying and handling',
  },
  'home.cases.notHandled': { scope: 'tour', titleKey: 'notHandledTitle', bodyKey: 'notHandledBody' },

  // ---- Case chat ----
  'home.cases.audio': {
    scope: 'element', selector: '#tutorialAudioRecorder', titleKey: 'audioTitle', descriptionHtml: audioControlsHtml, requires: ['reportsTalkToUsers'],
  },
  'home.cases.moderation': {
    scope: 'element', selector: '#tutorialParticipants', titleKey: 'moderateTitle', bodyKey: 'moderateBody', requires: ['reportsTalkToUsers'], guideAnchor: 'Handling abusive reporters',
  },
  'home.cases.messageMenu': {
    scope: 'element', selector: '#tutorialCaseChat', titleKey: 'msgMenuTitle', descriptionHtml: messageMenuHtml, requires: ['reportsTalkToUsers'],
  },
  'home.cases.messaging': {
    scope: 'tour', selector: '#tutorialCaseMessaging', titleKey: 'messagingTitle', bodyKey: 'messagingBody', requires: ['reportsTalkToUsers'], guideAnchor: 'The discussion system',
  },
  'home.cases.compose': {
    scope: 'element', selector: '#tutorialCaseCompose', titleKey: 'composeTitle', bodyKey: 'composeBody', requires: ['reportsTalkToUsers'], guideAnchor: 'The discussion system',
  },
  'home.cases.discussionNew': {
    scope: 'element', selector: '#tutorialDiscussionNew', titleKey: 'discussionNewTitle', bodyKey: 'discussionNewBody', requires: ['reportsTalkToUsers'], guideAnchor: 'The discussion system',
  },
  'home.cases.discussionSwitch': {
    scope: 'element', selector: '#tutorialDiscussionButtons', titleKey: 'discussionSwitchTitle', bodyKey: 'discussionSwitchBody', requires: ['reportsTalkToUsers'],
  },

  // ---- Tasks ----
  'home.tasks.list': {
    scope: 'element', selector: '#tutorialTasksList', titleKey: 'tasksColumnsTitle', descriptionHtml: tasksColumnsHtml, guideAnchor: 'The tasks list',
  },
  'home.tasks.tabs': {
    scope: 'element', selector: '#mine', titleKey: 'tasksTabsTitle', bodyKey: 'tasksTabsBody',
  },
  'home.tasks.teamsFilter': {
    scope: 'element', selector: '#tasksTeamsFilter', titleKey: 'tasksFilterTitle', bodyKey: 'tasksFilterBody',
  },
  'home.tasks.autoFilter': {
    scope: 'element', selector: '#tasksAutoFilter', titleKey: 'tasksAutoFilterTitle', bodyKey: 'tasksAutoFilterBody',
  },
  'home.tasks.create': {
    scope: 'element', selector: '#tutorialTasksNew', titleKey: 'tasksNewTitle', bodyKey: 'tasksNewBody', featureFlag: 'enableTaskCreation', guideAnchor: 'Creating a task',
  },
  'home.tasks.noneSelected': { scope: 'tour', titleKey: 'noTaskSelectedTitle', bodyKey: 'noTaskSelectedBody' },
  'home.tasks.details': {
    scope: 'element', selector: '#tutorialTaskDetails', titleKey: 'taskDetailsTitle', bodyKey: 'taskDetailsBody', guideAnchor: 'The task detail',
  },
  'home.tasks.urgency': {
    scope: 'element', selector: '#tutorialTaskUrgency', titleKey: 'taskUrgencyTitle', bodyKey: 'taskUrgencyBody',
  },
  'home.tasks.steps': {
    scope: 'element', selector: '#tutorialTaskSteps', titleKey: 'taskStepsTitle', bodyKey: 'taskStepsBody', guideAnchor: 'Moving a task along',
  },
  'home.tasks.controls': {
    scope: 'element', page: 'tasks/list', selector: '#tutorialCaseControls', titleKey: 'taskControlsTitle', bodyKey: 'taskControlsBody', guideAnchor: 'Moving a task along',
  },

  // ---- QR codes ----
  'qrs.list.table': {
    scope: 'element', selector: '#tutorialQrsTitle', titleKey: 'qrsListTitle', descriptionHtml: qrColumnsHtml, requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Your QR codes',
  },
  'qrs.list.add': {
    scope: 'element', selector: '#tutorialQrAdd', titleKey: 'qrAddTitle', bodyKey: 'qrAddBody', requires: ['qrEdit'], guideAnchor: 'Creating a template and its QR codes',
  },
  'qrs.list.displayToggles': {
    scope: 'element', selector: '#tutorialQrDisplayToggles', titleKey: 'qrTogglesTitle', descriptionHtml: qrDisplayTogglesHtml, requires: pagePrivilegesNeeded.qrs,
  },
  'qrs.list.folderControls': {
    scope: 'element', selector: '#tutorialQrFolderControls', titleKey: 'qrFolderControlsTitle', descriptionHtml: qrFolderControlsHtml, requires: ['qrEdit'], guideAnchor: 'Overview: folders and templates',
  },
  'qrs.list.selectAll': {
    scope: 'element', page: 'qrs/list', selector: '#tutorialQrSelectHeader', titleKey: 'qrListSelectAllTitle', bodyKey: 'qrListSelectAllBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Your QR codes',
  },
  'qrs.list.selectHint': { scope: 'tour', titleKey: 'qrListSelectTitle', bodyKey: 'qrListSelectBody' },
  'qrs.details': {
    scope: 'element', selector: '#qrDetails #settings', titleKey: 'qrsDetailsTitle', bodyKey: 'qrsDetailsBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Creating a template and its QR codes',
  },
  'qrs.details.template': {
    scope: 'element', selector: '#tutorialQrTemplate', titleKey: 'qrsDetailsTemplateTitle', bodyKey: 'qrsDetailsTemplateBody', requires: pagePrivilegesNeeded.qrs,
  },
  'qrs.details.status': {
    scope: 'element', selector: '#disableEnable', titleKey: 'qrsDetailsStatusTitle', bodyKey: 'qrsDetailsStatusBody', requires: ['qrManage', 'qrEdit'],
  },
  'qrs.details.cases': {
    scope: 'element', selector: '#tutorialQrCases', titleKey: 'qrsDetailsCasesTitle', bodyKey: 'qrsDetailsCasesBody', requires: pagePrivilegesNeeded.qrs,
  },
  'qrs.templates.list': {
    scope: 'element', selector: '#tutorialQrTemplatesTitle', titleKey: 'qrTemplatesListTitle', bodyKey: 'qrTemplatesListBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Overview: folders and templates',
  },
  'qrs.templates.selectHint': { scope: 'tour', titleKey: 'qrTemplateSelectTitle', bodyKey: 'qrTemplateSelectBody' },
  'qrs.templates.details': {
    scope: 'element', selector: '#qrTemplateDetails #settings', titleKey: 'qrTemplateDetailsTitle', bodyKey: 'qrTemplateDetailsBody', requires: pagePrivilegesNeeded.qrs, guideAnchor: 'Creating a template and its QR codes',
  },
  'qrs.printing.panel': {
    scope: 'element', selector: '#tutorialQrsPrintingTitle', titleKey: 'qrsPrintingTitle', bodyKey: 'qrsPrintingBody', requires: ['qrManage'], guideAnchor: 'Printing',
  },
  'qrs.printing.printTab': {
    scope: 'element', selector: '#qrsPrinting #print', titleKey: 'qrsPrintingPrintTabTitle', bodyKey: 'qrsPrintingPrintTabBody', requires: ['qrManage'],
  },
  'qrs.printing.signTab': {
    scope: 'element', selector: '#qrsPrinting #format', titleKey: 'qrsPrintingSignTabTitle', bodyKey: 'qrsPrintingSignTabBody', requires: ['qrManage'],
  },
  'qrs.printing.layoutTab': {
    scope: 'element', selector: '#qrsPrinting #layout', titleKey: 'qrsPrintingLayoutTabTitle', bodyKey: 'qrsPrintingLayoutTabBody', requires: ['qrManage'],
  },
  'qrs.printing.selection': {
    scope: 'element', page: 'qrs/printing', selector: '#tutorialQrSelectHeader', titleKey: 'qrsPrintingSelectTitle', bodyKey: 'qrsPrintingSelectBody', requires: ['qrManage'],
  },
  'qrs.printing.print': {
    scope: 'element', selector: '#tutorialQrPrintButton', titleKey: 'qrsPrintingPrintButtonTitle', bodyKey: 'qrsPrintingPrintButtonBody', requires: ['qrManage'], guideAnchor: 'Printing',
  },

  // ---- Poll editor (popup) ----
  'polls.edit.name': { scope: 'tour', titleKey: 'pollEditNameTitle', bodyKey: 'pollEditNameBody' },
  'polls.edit.items': { scope: 'tour', titleKey: 'pollEditItemsTitle', bodyKey: 'pollEditItemsBody' },
  'polls.edit.form': { scope: 'tour', titleKey: 'pollEditFormTitle', bodyKey: 'pollEditFormBody' },
  'polls.edit.params': { scope: 'tour', titleKey: 'pollEditParamsTitle', bodyKey: 'pollEditParamsBody' },
  'polls.edit.id': { scope: 'tour', titleKey: 'pollEditIdTitle', bodyKey: 'pollEditIdBody' },
  'polls.edit.save': { scope: 'tour', titleKey: 'pollEditSaveTitle', bodyKey: 'pollEditSaveBody' },

  // ---- Stats ----
  'stats.controls': {
    scope: 'element', selector: '#tutorialStatsControls', titleKey: 'statsControlsTitle', bodyKey: 'statsControlsBody', requires: pagePrivilegesNeeded.stats, guideAnchor: 'Statistics',
  },
  'stats.overlays': {
    scope: 'element', selector: '#tutorialStatsOverlays', titleKey: 'statsOverlaysTitle', bodyKey: 'statsOverlaysBody', requires: pagePrivilegesNeeded.stats, guideAnchor: 'Statistics',
  },
  'stats.breakdown': {
    scope: 'element', selector: '#tutorialStatsBreakdown', titleKey: 'statsBreakdownTitle', bodyKey: 'statsBreakdownBody', requires: pagePrivilegesNeeded.stats, guideAnchor: 'Statistics',
  },

  // ---- Polls ----
  'polls.list': {
    scope: 'element', selector: '#tutorialPollsTitle', titleKey: 'pollsListTitle', bodyKey: 'pollsListBody', requires: pagePrivilegesNeeded.polls, guideAnchor: 'Creating a poll and managing the library',
  },
  'polls.list.add': {
    scope: 'element', selector: '#addPoll', titleKey: 'pollAddTitle', bodyKey: 'pollAddBody', requires: ['pollEdit'], guideAnchor: 'Creating a poll and managing the library',
  },
  'polls.details': {
    scope: 'element', selector: '#pollDetails #settings', titleKey: 'pollDetailsTitle', bodyKey: 'pollDetailsBody', requires: pagePrivilegesNeeded.polls, guideAnchor: 'Creating a poll and managing the library',
  },
  'polls.details.qrTab': {
    scope: 'element', selector: '#pollDetails #qrCode', titleKey: 'pollQrTabTitle', bodyKey: 'pollQrTabBody', requires: pagePrivilegesNeeded.polls,
  },
  'polls.results.selector': {
    scope: 'element', selector: '#pollSel', titleKey: 'pollResultsSelectTitle', bodyKey: 'pollResultsSelectBody', requires: ['pollViewResults'], guideAnchor: 'Results',
  },
  'polls.results.count': {
    scope: 'element', selector: '#pollResultsAnswerCnt', titleKey: 'pollResultsCountTitle', bodyKey: 'pollResultsCountBody', requires: ['pollViewResults'], guideAnchor: 'Results',
  },

  // ---- Publications ----
  'comm.list': {
    scope: 'element', selector: '#tutorialCommListTitle', titleKey: 'commsListTitle', bodyKey: 'commsListBody', requires: pagePrivilegesNeeded.comms, guideAnchor: 'Overview',
  },
  'comm.list.add': {
    scope: 'element', selector: '#tutorialCommAdd', titleKey: 'commsAddTitle', bodyKey: 'commsAddBody', requires: ['infoEdit'], guideAnchor: 'Overview',
  },
  'comm.preview': {
    scope: 'element', selector: '#tutorialCommPreview', titleKey: 'commsPreviewTitle', bodyKey: 'commsPreviewBody', requires: pagePrivilegesNeeded.comms,
  },
  'comm.details': {
    scope: 'element', selector: '#commDetails #details', titleKey: 'commsDetailsTitle', bodyKey: 'commsDetailsBody', requires: pagePrivilegesNeeded.comms,
  },
  'comm.details.feedback': {
    scope: 'element', selector: '#tutorialCommFeedback', titleKey: 'commsFeedbackTitle', bodyKey: 'commsFeedbackBody', requires: pagePrivilegesNeeded.comms, guideAnchor: 'Feedback',
  },
  'comm.feedbacks': {
    scope: 'element', selector: '#tutorialFeedbacksTitle', titleKey: 'feedbacksTitle', bodyKey: 'feedbacksBody', requires: pagePrivilegesNeeded.comms, guideAnchor: 'Feedback',
  },

  // ---- Responders ----
  'responders.teams.list': {
    scope: 'element', selector: '#tutorialTeamsTitle', titleKey: 'teamsListTitle', bodyKey: 'teamsListBody', requires: pagePrivilegesNeeded.responders, guideAnchor: 'Teams — overview',
  },
  'responders.teams.add': {
    scope: 'element', selector: '#addStaff', titleKey: 'teamsAddTitle', bodyKey: 'teamsAddBody', requires: pagePrivilegesNeeded.responders, guideAnchor: 'Managing your accounts',
  },
  'responders.missions': {
    scope: 'element', selector: '#tutorialMissionsTitle', titleKey: 'missionsTitle', bodyKey: 'missionsBody', requires: pagePrivilegesNeeded.responders, guideAnchor: 'Missions',
  },

  // ---- Users ----
  'users.members.tab': {
    scope: 'element', selector: '#members', titleKey: 'usersMembersTitle', bodyKey: 'usersMembersBody', requires: pagePrivilegesNeeded.users, guideAnchor: 'End users: anonymous and members',
  },
  'users.domains.tab': {
    scope: 'element', selector: '#domains', titleKey: 'usersDomainsTitle', bodyKey: 'usersDomainsBody', requires: pagePrivilegesNeeded.users, guideAnchor: 'End users: anonymous and members',
  },

  // ---- Partners ----
  'partners.list': {
    scope: 'element', selector: '#tutorialPartnersTitle', titleKey: 'partnersListTitle', bodyKey: 'partnersListBody', guideAnchor: 'Partners',
  },
  'partners.list.add': {
    scope: 'element', selector: '#addPartner', titleKey: 'partnersAddTitle', bodyKey: 'partnersAddBody', requires: ['partnerEdit'], guideAnchor: 'Partnerships you send',
  },

  // ---- Settings ----
  'settings.custom.panel': {
    scope: 'element', selector: '#tutorialCustomizationTitle', titleKey: 'customizationIntroTitle', bodyKey: 'customizationIntroBody', requires: pagePrivilegesNeeded.customization, guideAnchor: 'Visual identity',
  },
  'settings.custom.locations': {
    scope: 'element', selector: '#tutorialCustomLocations', titleKey: 'customizationLocationsTitle', bodyKey: 'customizationLocationsBody', requires: pagePrivilegesNeeded.customization, guideAnchor: 'Folder customization',
  },
  'settings.custom.branding': {
    scope: 'element', selector: '#tutorialCustomBranding', titleKey: 'customizationBrandingTitle', bodyKey: 'customizationBrandingBody', requires: pagePrivilegesNeeded.customization, guideAnchor: 'Logos',
  },
  'settings.licenses.list': {
    scope: 'element', selector: '#tutorialLicensesList', titleKey: 'licensesListTitle', bodyKey: 'licensesListBody', guideAnchor: 'Licenses and privileges',
  },
  'settings.licenses.sites': {
    scope: 'element', selector: '#sites', titleKey: 'licenseSitesTitle', bodyKey: 'licenseSitesBody', guideAnchor: 'Licenses and privileges',
  },
  'settings.secondFactor': {
    scope: 'element', selector: '#tutorialSecondFactorSettings', titleKey: 'settingsSecondFactorTitle', bodyKey: 'settingsSecondFactorBody', guideAnchor: 'Two-factor login', requires: ['admin'],
  },

  // ---- Profile ----
  'profile.language': {
    scope: 'element', selector: '#language', titleKey: 'profileLangTitle', bodyKey: 'profileLangBody', guideAnchor: 'Language',
  },
  'profile.password': {
    scope: 'element', selector: '#password', titleKey: 'profilePasswordTitle', bodyKey: 'profilePasswordBody', guideAnchor: 'Password',
  },
  'profile.security.secondFactor': {
    scope: 'element', selector: '#tutorialSecondFactorTitle', titleKey: 'securitySecondFactorTitle', bodyKey: 'securitySecondFactorBody', guideAnchor: 'Two-factor login',
  },
  'profile.security.methods': {
    scope: 'element', selector: '#tutorialSecondFactorMethods', titleKey: 'securityMethodsTitle', bodyKey: 'securityMethodsBody', guideAnchor: 'Two-factor login',
  },
  'profile.security.add': {
    scope: 'element', selector: '#tutorialSecondFactorAdd', titleKey: 'securityAddTitle', bodyKey: 'securityAddBody', guideAnchor: 'Two-factor login',
  },
  'profile.security.recoveryCodes': {
    scope: 'element', selector: '#tutorialRecoveryCodes', titleKey: 'securityRecoveryCodesTitle', bodyKey: 'securityRecoveryCodesBody', guideAnchor: 'Two-factor login',
  },
  'profile.logout': {
    scope: 'element', selector: '#logout', titleKey: 'profileLogoutTitle', bodyKey: 'profileLogoutBody', guideAnchor: 'Your account',
  },
  'profile.devices.list': {
    scope: 'element', selector: '#tutorialDevicesTitle', titleKey: 'devicesListTitle', bodyKey: 'devicesListBody', guideAnchor: 'Managing your connected devices',
  },
  'profile.devices.this': {
    scope: 'element', selector: '#tutorialThisDevice', titleKey: 'devicesThisTitle', bodyKey: 'devicesThisBody',
  },
  'profile.devices.connect': {
    scope: 'element', selector: '#toggleConnect', titleKey: 'devicesAddTitle', bodyKey: 'devicesAddBody', guideAnchor: 'Connecting your phone to your account',
  },
  'profile.devices.disconnect': {
    scope: 'element', selector: '#disconnect', titleKey: 'devicesDisconnectTitle', bodyKey: 'devicesDisconnectBody', guideAnchor: 'Managing your connected devices',
  },
};

export default explainRegistry;
